import { useState, useEffect, useRef, useCallback } from "react"
import {
  Search,
  X,
  ChevronDown,
  ChevronRight,
  Mail,
  Briefcase,
  Clock,
  Zap,
  UserCheck,
  Plus,
  Archive,
  Loader2,
} from "lucide-react"
import AddLeadModal from "./AddLeadModal"
import LeadViewer from "./LeadViewer"
import ArchiveLeadModal from "./ArchiveLeadModal"
import EmailModalMany from "./EmailLeadMany"
import EmailModal from "./EmailLeadSingle"

// ── Design tokens (shared with JobIdInterviews / EmailModal) ──────────────────
const FF =
  "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const MONO = "'IBM Plex Mono', monospace"

const T = {
  border: "0.5px solid #e5e7eb",
  borderHover: "0.5px solid #d1d5db",
  radius: 8,
  radiusCard: 10,
  bg: "#fff",
  bgPage: "#f5f5f0",
  bgMuted: "#fafaf9",
  text: "#111827",
  textMuted: "#374151",
  textTertiary: "#6b7280",
  shadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
}

const GRID = "28px minmax(0,2fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) 96px"

const SECTIONS = [
  { key: "new", label: "New leads", icon: Zap },
  { key: "reachedOut", label: "Reached out", icon: Mail },
  { key: "responded", label: "Responded", icon: UserCheck },
  { key: "archived", label: "Archived", icon: Archive },
]

const POLL_INTERVAL = 3000

// ── Helpers ───────────────────────────────────────────────────────────────────
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#6366f1,#8b5cf6)",
  "linear-gradient(135deg,#0ea5e9,#6366f1)",
  "linear-gradient(135deg,#10b981,#0ea5e9)",
  "linear-gradient(135deg,#f59e0b,#ef4444)",
  "linear-gradient(135deg,#ec4899,#8b5cf6)",
  "linear-gradient(135deg,#14b8a6,#6366f1)",
  "linear-gradient(135deg,#f97316,#eab308)",
  "linear-gradient(135deg,#06b6d4,#10b981)",
]
const getAvatarGradient = (name = "") =>
  AVATAR_GRADIENTS[
    name.split("").reduce((a, c) => a + c.charCodeAt(0), 0) %
      AVATAR_GRADIENTS.length
  ]
const getInitials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase()

const createLeadContext = ({ lead, jobListing }) => ({
  candidate: {
    firstName: lead?.applicantName?.split(" ")[0] ?? null,
    fullName: lead?.applicantName ?? null,
  },
  job: {
    role: jobListing?.role ?? null,
    company: jobListing?.organizationName ?? null,
  },
})

const getScoreConfig = (score) => {
  if (score == null)
    return {
      bg: "#f9fafb",
      color: "#9ca3af",
      border: "#e5e7eb",
      dot: "#d1d5db",
    }
  if (score >= 75)
    return {
      bg: "#ecfdf5",
      color: "#065f46",
      border: "#a7f3d0",
      dot: "#10b981",
    }
  if (score >= 50)
    return {
      bg: "#eff6ff",
      color: "#1d4ed8",
      border: "#bfdbfe",
      dot: "#3b82f6",
    }
  if (score >= 25)
    return {
      bg: "#fffbeb",
      color: "#92400e",
      border: "#fde68a",
      dot: "#f59e0b",
    }
  return { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca", dot: "#ef4444" }
}

// ── Avatar ────────────────────────────────────────────────────────────────────
const Avatar = ({ name, size = 32 }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: 7,
      background: getAvatarGradient(name),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      color: "#fff",
      fontSize: size < 36 ? 11 : 13,
      fontWeight: 700,
      fontFamily: FF,
      letterSpacing: "0.02em",
    }}
  >
    {getInitials(name)}
  </div>
)

// ── Checkbox (same style as before, now inline-styled) ────────────────────────
const Checkbox = ({ checked, onClick }) => (
  <div
    onClick={(e) => {
      e.stopPropagation()
      onClick?.(e)
    }}
    style={{
      width: 16,
      height: 16,
      borderRadius: 4,
      border: `1.5px solid ${checked ? "#111827" : "#d1d5db"}`,
      background: checked ? "#111827" : T.bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      cursor: "pointer",
      transition: "border-color 0.12s, background 0.12s",
    }}
  >
    {checked && (
      <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
        <polyline
          points="2,6 5,9 10,3"
          stroke="white"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )}
  </div>
)

// ── Action button (identical to JobIdInterviews ActionBtn) ────────────────────
const ActionBtn = ({
  onClick,
  children,
  variant = "ghost",
  style: extraStyle,
  ...rest
}) => {
  const variants = {
    ghost: { background: T.bgMuted, color: T.textMuted, border: T.border },
    dark: { background: "#111827", color: "#fff", border: "none" },
    outline: { background: T.bg, color: T.textMuted, border: T.border },
    danger: {
      background: "#fef2f2",
      color: "#b91c1c",
      border: "0.5px solid #fecaca",
    },
  }
  const s = variants[variant] || variants.ghost
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: 32,
        padding: "0 12px",
        borderRadius: T.radius,
        border: s.border,
        background: s.background,
        color: s.color,
        fontSize: 12,
        fontWeight: 600,
        fontFamily: FF,
        letterSpacing: "-0.01em",
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "opacity 0.12s, background 0.12s",
        outline: "none",
        ...extraStyle,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = "0.82"
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = "1"
      }}
      {...rest}
    >
      {children}
    </button>
  )
}

// ── Column headers ────────────────────────────────────────────────────────────
const TableHeader = ({ allChecked, onToggleAll }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: GRID,
      alignItems: "center",
      padding: "7px 16px",
      borderBottom: T.border,
      background: "#fdfcfb",
    }}
  >
    <Checkbox checked={allChecked} onClick={onToggleAll} />
    {["Lead", "Match", "Experience", "Notice", ""].map((h, i) => (
      <span
        key={i}
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: T.textTertiary,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          fontFamily: MONO,
          textAlign: i === 4 ? "right" : "left",
        }}
      >
        {h}
      </span>
    ))}
  </div>
)

// ── Score badge ───────────────────────────────────────────────────────────────
const ScoreBadge = ({ lead }) => {
  const isProcessing = lead.matchingScoreStatus === "processing"
  const score = lead.matchingScore?.finalScore ?? null

  if (isProcessing) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "3px 8px",
          borderRadius: T.radius,
          background: "#f9fafb",
          color: T.textTertiary,
          border: T.border,
          fontSize: 12,
          fontWeight: 600,
          fontFamily: MONO,
        }}
      >
        <Loader2
          size={10}
          style={{
            animation: "leads-spin 0.8s linear infinite",
            flexShrink: 0,
          }}
        />
        Processing
      </span>
    )
  }

  if (score == null) {
    return (
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: T.textTertiary,
          fontFamily: FF,
        }}
      >
        —
      </span>
    )
  }

  const cfg = getScoreConfig(score)
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: cfg.dot,
          flexShrink: 0,
        }}
      />
      <div>
        <p
          style={{
            margin: 0,
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: cfg.color,
            fontFamily: MONO,
            lineHeight: 1,
          }}
        >
          {lead.matchingScore?.matchLevel?.replace(/_/g, " ") || "—"}
        </p>
        <p
          style={{
            margin: "3px 0 0",
            fontSize: 13,
            fontWeight: 700,
            color: T.text,
            fontFamily: FF,
            lineHeight: 1,
          }}
        >
          {score}%
        </p>
      </div>
    </div>
  )
}

// ── Lead row ──────────────────────────────────────────────────────────────────
const LeadRow = ({
  lead,
  index,
  total,
  checked,
  onCheck,
  onView,
  onMessage,
  isSelected,
}) => (
  <div
    onClick={() => onCheck(lead.applicantId)}
    style={{
      display: "grid",
      gridTemplateColumns: GRID,
      alignItems: "center",
      padding: "11px 16px",
      borderBottom: index !== total - 1 ? T.border : "none",
      borderLeft: isSelected ? "2px solid #3b82f6" : "2px solid transparent",
      background: isSelected
        ? "rgba(59,130,246,0.04)"
        : checked
          ? T.bgMuted
          : "transparent",
      cursor: "pointer",
      transition: "background 0.1s",
    }}
    onMouseEnter={(e) => {
      if (!isSelected && !checked) e.currentTarget.style.background = T.bgMuted
    }}
    onMouseLeave={(e) => {
      if (!isSelected && !checked)
        e.currentTarget.style.background = "transparent"
    }}
  >
    {/* Checkbox */}
    <Checkbox checked={checked} onClick={() => onCheck(lead.applicantId)} />

    {/* Name + title */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        minWidth: 0,
        paddingRight: 12,
      }}
    >
      <Avatar name={lead.applicantName} />
      <div style={{ minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 600,
            color: T.text,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontFamily: FF,
          }}
        >
          {lead.applicantName}
        </p>
        <p
          style={{
            margin: "2px 0 0",
            fontSize: 12,
            fontWeight: 500,
            color: T.textTertiary,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontFamily: FF,
          }}
        >
          {lead.personal_information?.currentJobTitle &&
          lead.personal_information?.currentCompany
            ? `${lead.personal_information.currentJobTitle} · ${lead.personal_information.currentCompany}`
            : lead.personal_information?.currentJobTitle ||
              lead.personal_information?.currentCompany ||
              "—"}
        </p>
      </div>
    </div>

    {/* Score */}
    <ScoreBadge lead={lead} />

    {/* Experience */}
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <Briefcase size={12} color="#d1d5db" style={{ flexShrink: 0 }} />
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: T.textMuted,
          fontFamily: FF,
        }}
      >
        {lead.personal_information?.yearsExperience
          ? `${lead.personal_information.yearsExperience} yrs`
          : "—"}
      </span>
    </div>

    {/* Notice */}
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <Clock size={12} color="#d1d5db" style={{ flexShrink: 0 }} />
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: T.textMuted,
          fontFamily: FF,
        }}
      >
        {lead.job_preferences?.noticePeriod || "—"}
      </span>
    </div>

    {/* Actions */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 5,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Mail icon button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onMessage?.(lead)
        }}
        title="Send email"
        style={{
          width: 28,
          height: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: T.bg,
          border: T.border,
          borderRadius: T.radius - 1,
          cursor: "pointer",
          color: T.textTertiary,
          transition: "all 0.12s",
          outline: "none",
          flexShrink: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#111827"
          e.currentTarget.style.color = "#fff"
          e.currentTarget.style.borderColor = "#111827"
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = T.bg
          e.currentTarget.style.color = T.textTertiary
          e.currentTarget.style.borderColor = "#e5e7eb"
        }}
      >
        <Mail size={11} strokeWidth={2} />
      </button>

      {/* View button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onView(lead)
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          height: 28,
          padding: "0 10px",
          borderRadius: T.radius - 1,
          border: isSelected ? "0.5px solid #bfdbfe" : T.border,
          background: isSelected ? "rgba(59,130,246,0.08)" : T.bgMuted,
          color: isSelected ? "#1d4ed8" : T.textMuted,
          fontSize: 12,
          fontWeight: 700,
          fontFamily: FF,
          letterSpacing: "0.03em",
          cursor: "pointer",
          outline: "none",
          transition: "all 0.12s",
          whiteSpace: "nowrap",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = "0.82"
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = "1"
        }}
      >
        View <ChevronRight size={10} strokeWidth={2.5} />
      </button>
    </div>
  </div>
)

// ── Section card (mirrors JobIdInterviews SectionTable) ───────────────────────
const SectionGroup = ({
  section,
  leads,
  selectedIds,
  onCheck,
  onToggleAll,
  isLoading,
  onView,
  onMessage,
  viewingId,
}) => {
  const [collapsed, setCollapsed] = useState(section.key === "archived")
  const sectionIds = leads.map((l) => l.applicantId)
  const allChecked =
    sectionIds.length > 0 && sectionIds.every((id) => selectedIds.has(id))
  const Icon = section.icon

  return (
    <div
      style={{
        background: T.bg,
        borderRadius: T.radiusCard,
        border: T.border,
        boxShadow: T.shadow,
        overflow: "hidden",
      }}
    >
      {/* Section header — same strip as JobIdInterviews */}
      <button
        onClick={() => setCollapsed((c) => !c)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "11px 16px",
          background: T.bgMuted,
          border: "none",
          borderBottom: collapsed ? "none" : T.border,
          cursor: "pointer",
          transition: "background 0.12s",
          outline: "none",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#f3f2f0"
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = T.bgMuted
        }}
      >
        {collapsed ? (
          <ChevronRight size={13} strokeWidth={2.5} color="#9ca3af" />
        ) : (
          <ChevronDown size={13} strokeWidth={2.5} color="#9ca3af" />
        )}
        <Icon size={14} color="#6b7280" style={{ flexShrink: 0 }} />
        <span
          style={{
            flex: 1,
            textAlign: "left",
            fontSize: 13,
            fontWeight: 700,
            color: T.text,
            letterSpacing: "-0.01em",
            fontFamily: FF,
          }}
        >
          {section.label}
        </span>

        {/* Loading spinner inline with count */}
        {isLoading && section.key === "new" && (
          <Loader2
            size={12}
            color="#9ca3af"
            style={{
              animation: "leads-spin 0.8s linear infinite",
              flexShrink: 0,
            }}
          />
        )}

        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: T.textTertiary,
            background: "#f3f4f6",
            border: T.border,
            borderRadius: 5,
            padding: "1px 7px",
            fontFamily: MONO,
          }}
        >
          {leads.length}
        </span>
      </button>

      {!collapsed && (
        <>
          <TableHeader
            allChecked={allChecked}
            onToggleAll={() => onToggleAll(sectionIds, !allChecked)}
          />
          {leads.length === 0 ? (
            <div
              style={{
                padding: "20px 16px",
                fontSize: 13,
                fontWeight: 500,
                color: T.textTertiary,
                fontFamily: FF,
                fontStyle: "italic",
                textAlign: "center",
              }}
            >
              No leads in this stage
            </div>
          ) : (
            leads.map((lead, idx, arr) => (
              <LeadRow
                key={lead.applicantId}
                lead={lead}
                index={idx}
                total={arr.length}
                checked={selectedIds.has(lead.applicantId)}
                onCheck={onCheck}
                onView={onView}
                onMessage={onMessage}
                isSelected={viewingId === lead.applicantId}
              />
            ))
          )}
        </>
      )}
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
const Leads = ({ jobId, orgId, jobListing, onScheduleInterview }) => {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [showAddModal, setShowAddModal] = useState(false)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [emailTarget, setEmailTarget] = useState(null)
  const [emailManyRecipients, setEmailManyRecipients] = useState(null)
  const [leads, setLeads] = useState(jobListing?.leads || [])
  const [isFetching, setIsFetching] = useState(false)
  const [viewingLead, setViewingLead] = useState(null)
  const pollIntervalRef = useRef(null)

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchLeads = useCallback(
    async ({ showLoader = false } = {}) => {
      try {
        if (showLoader) setIsFetching(true)
        const res = await fetch(
          `https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${orgId}/${jobId}/leads`,
        )
        if (!res.ok) throw new Error(`Fetch leads failed (${res.status})`)
        const json = await res.json()
        const list = Array.isArray(json)
          ? json
          : (json.data ?? json.leads ?? [])
        setLeads(list)
        setViewingLead((prev) =>
          prev
            ? (list.find((l) => l.applicantId === prev.applicantId) ?? prev)
            : null,
        )
      } catch (err) {
        console.error("Error fetching leads:", err)
      } finally {
        if (showLoader) setIsFetching(false)
      }
    },
    [orgId, jobId],
  )

  const handleMessageCandidate = useCallback(
    (lead) => {
      setEmailTarget({ lead, context: createLeadContext({ lead, jobListing }) })
    },
    [jobListing],
  )

  const handleEmailSelected = useCallback(() => {
    const selectedLeads = leads.filter((l) => selectedIds.has(l.applicantId))

    if (selectedLeads.length === 1) {
      const lead = selectedLeads[0]
      setEmailTarget({
        lead,
        context: createLeadContext({ lead, jobListing }),
        onSendComplete: async () => {
          if (lead.leadStatus === "new") {
            await fetch(
              `https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${orgId}/${jobId}/candidates/${lead.applicantId}/status`,
              {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ type: "lead", status: "reachedOut" }),
              },
            )
            setLeads((prev) =>
              prev.map((l) =>
                l.applicantId === lead.applicantId
                  ? { ...l, leadStatus: "reachedOut" }
                  : l,
              ),
            )
          }
          setEmailTarget(null)
        },
      })
    } else {
      setEmailManyRecipients({
        recipients: selectedLeads.map((lead) => ({
          applicantId: lead.applicantId,
          name: lead.applicantName,
          email: lead.applicantEmail,
          context: createLeadContext({ lead, jobListing }),
        })),
        onSendComplete: async () => {
          const newLeads = selectedLeads.filter((l) => l.leadStatus === "new")
          await Promise.all(
            newLeads.map((lead) =>
              fetch(
                `https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${orgId}/${jobId}/candidates/${lead.applicantId}/status`,
                {
                  method: "PATCH",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ type: "lead", status: "reachedOut" }),
                },
              ),
            ),
          )
          setLeads((prev) =>
            prev.map((l) =>
              selectedIds.has(l.applicantId) && l.leadStatus === "new"
                ? { ...l, leadStatus: "reachedOut" }
                : l,
            ),
          )
          setEmailManyRecipients(null)
        },
      })
    }
  }, [leads, selectedIds, jobListing, orgId, jobId])

  const handleArchiveConfirm = useCallback(
    async ({ selectedIds, reason, sendEmail }) => {
      const ids = [...selectedIds]
      const selectedLeads = leads.filter((l) => selectedIds.has(l.applicantId))

      setShowArchiveModal(false)

      if (sendEmail && selectedLeads.length === 1) {
        // Single lead + email: archive happens AFTER email is sent
        const lead = selectedLeads[0]
        setEmailTarget({
          lead,
          context: createLeadContext({ lead, jobListing }),
          confirmLabel: "Archive & Send",
          onSendComplete: async () => {
            await fetch(
              `https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${orgId}/${jobId}/candidates/${lead.applicantId}/status`,
              {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ type: "lead", status: "archived" }),
              },
            )
            setLeads((prev) =>
              prev.map((l) =>
                l.applicantId === lead.applicantId
                  ? { ...l, leadStatus: "archived" }
                  : l,
              ),
            )
            setEmailTarget(null)
          },
        })
      } else if (sendEmail && selectedLeads.length > 1) {
        // Bulk + email: archive happens AFTER emails are sent
        setEmailManyRecipients({
          recipients: selectedLeads.map((lead) => ({
            applicantId: lead.applicantId,
            name: lead.applicantName,
            email: lead.applicantEmail,
            context: createLeadContext({ lead, jobListing }),
          })),
          confirmLabel: "Archive & Send",
          onSendComplete: async () => {
            await Promise.all(
              ids.map((userId) =>
                fetch(
                  `https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${orgId}/${jobId}/candidates/${userId}/status`,
                  {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ type: "lead", status: "archived" }),
                  },
                ),
              ),
            )
            setLeads((prev) =>
              prev.map((l) =>
                selectedIds.has(l.applicantId)
                  ? { ...l, leadStatus: "archived" }
                  : l,
              ),
            )
            setEmailManyRecipients(null)
          },
        })
      } else {
        // No email: archive immediately
        await Promise.all(
          ids.map((userId) =>
            fetch(
              `https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${orgId}/${jobId}/candidates/${userId}/status`,
              {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ type: "lead", status: "archived" }),
              },
            ),
          ),
        )
        setLeads((prev) =>
          prev.map((l) =>
            selectedIds.has(l.applicantId)
              ? { ...l, leadStatus: "archived" }
              : l,
          ),
        )
      }

      setSelectedIds(new Set())
    },
    [leads, jobListing, orgId, jobId],
  )

  // ── Polling ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const hasProcessing = leads.some(
      (l) => l.matchingScoreStatus === "processing",
    )
    if (hasProcessing && !pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(() => fetchLeads(), POLL_INTERVAL)
    } else if (!hasProcessing && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }
  }, [leads, fetchLeads])

  useEffect(() => {
    if (jobListing?.leads) setLeads(jobListing.leads)
  }, [jobListing])

  // ── Bucketing ────────────────────────────────────────────────────────────
  const bucketed = {
    new: leads.filter((l) => l.leadStatus === "new"),
    reachedOut: leads.filter((l) => l.leadStatus === "reachedOut"),
    responded: leads.filter((l) => l.leadStatus === "responded"),
    archived: leads.filter((l) => l.leadStatus === "archived"),
  }

  const hasSelection = selectedIds.size > 0

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleSection = (ids, select) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => (select ? next.add(id) : next.delete(id)))
      return next
    })
  }

  const handleView = (lead) => {
    setViewingLead((prev) =>
      prev?.applicantId === lead.applicantId ? null : lead,
    )
  }

  const filteredSections = SECTIONS.map((section) => ({
    ...section,
    leads: searchQuery.trim()
      ? bucketed[section.key].filter((l) => {
          const q = searchQuery.toLowerCase()
          return (
            l.applicantName?.toLowerCase().includes(q) ||
            l.personal_information?.currentJobTitle
              ?.toLowerCase()
              .includes(q) ||
            l.personal_information?.currentCompany?.toLowerCase().includes(q)
          )
        })
      : bucketed[section.key],
  }))

  return (
    <div
      style={{
        fontFamily: FF,
        background: T.bgPage,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        @keyframes leads-spin { to { transform: rotate(360deg); } }
        .leads-scroll::-webkit-scrollbar { display: none; }
        .leads-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* ── Top bar — mirrors EmailModal header strip ── */}
      <div
        style={{
          background: T.bg,
          borderBottom: T.border,
          padding: "13px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        {/* Left: title + count */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Dot accent — same as EmailModal / DetailPanel header */}
          <div
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#111827",
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: T.textTertiary,
              fontFamily: MONO,
            }}
          >
            Leads
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: T.textTertiary,
              background: "#f3f4f6",
              border: T.border,
              borderRadius: 5,
              padding: "1px 7px",
              fontFamily: MONO,
            }}
          >
            {leads.length}
          </span>
        </div>

        {/* Right: search + actions */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Search input — same height/radius as EmailModal's subject row */}
          <div style={{ position: "relative", height: 34 }}>
            <div
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Search size={13} color={T.textTertiary} />
            </div>
            <input
              type="text"
              placeholder="Search leads…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                height: "100%",
                paddingLeft: 30,
                paddingRight: searchQuery ? 28 : 12,
                width: 200,
                border: T.border,
                borderRadius: T.radius,
                fontSize: 13,
                fontWeight: 500,
                color: T.text,
                fontFamily: FF,
                background: T.bg,
                outline: "none",
                transition: "border-color 0.12s",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#9ca3af"
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e5e7eb"
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: T.textTertiary,
                  display: "flex",
                  alignItems: "center",
                  padding: 0,
                }}
              >
                <X size={11} />
              </button>
            )}
          </div>

          {hasSelection && (
            <ActionBtn variant="outline" onClick={handleEmailSelected}>
              <Mail size={12} strokeWidth={2} />
              Email{selectedIds.size > 1 ? ` (${selectedIds.size})` : ""}
            </ActionBtn>
          )}

          {/* Archive — only when rows are selected */}
          {hasSelection && (
            <ActionBtn
              variant="outline"
              onClick={() => setShowArchiveModal(true)}
            >
              <Archive size={12} strokeWidth={2} />
              Archive{selectedIds.size > 1 ? ` (${selectedIds.size})` : ""}
            </ActionBtn>
          )}

          {/* Add lead — dark CTA, matches EmailModal's send button */}
          <ActionBtn
            variant="dark"
            onClick={() => setShowAddModal(true)}
            style={{ height: 34 }}
          >
            <Plus size={12} strokeWidth={2.5} />
            Add lead
          </ActionBtn>
        </div>
      </div>

      {/* ── Scrollable list ── */}
      <div
        className="leads-scroll"
        style={{ flex: 1, overflowY: "auto", padding: "20px 20px 48px" }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {filteredSections.map((section) => (
            <SectionGroup
              key={section.key}
              section={section}
              leads={section.leads}
              selectedIds={selectedIds}
              onCheck={toggleOne}
              onToggleAll={toggleSection}
              isLoading={isFetching}
              onView={handleView}
              onMessage={handleMessageCandidate}
              viewingId={viewingLead?.applicantId}
            />
          ))}
        </div>
      </div>

      {/* ── LeadViewer overlay ── */}
      {viewingLead && (
        <LeadViewer
          orgId={orgId}
          jobId={jobId}
          selectedLead={viewingLead}
          jobListing={jobListing}
          onMessageLead={(lead) => {
            setEmailTarget({
              lead,
              context: createLeadContext({ lead, jobListing }),
            })
          }}
          onScheduleInterview={onScheduleInterview}
          onNoteSaved={(notes) => {
            setLeads((prev) =>
              prev.map((l) =>
                l.applicantId === viewingLead.applicantId
                  ? { ...l, internalNotes: notes }
                  : l,
              ),
            )
            setViewingLead((prev) => ({ ...prev, internalNotes: notes }))
          }}
          onClose={() => setViewingLead(null)}
        />
      )}

      {/* ── Modals ── */}
      {showAddModal && (
        <AddLeadModal
          jobId={jobId}
          orgId={orgId}
          onClose={() => setShowAddModal(false)}
          onSuccess={async () => {
            await fetchLeads({ showLoader: true })
            setShowAddModal(false)
          }}
        />
      )}

      {showArchiveModal && (
        <ArchiveLeadModal
          selectedIds={selectedIds}
          jobListing={jobListing}
          onClose={() => setShowArchiveModal(false)}
          onArchive={handleArchiveConfirm}
        />
      )}

      {emailManyRecipients && (
        <EmailModalMany
          isOpen
          recipients={emailManyRecipients.recipients}
          confirmLabel={emailManyRecipients.confirmLabel}
          onSendComplete={emailManyRecipients.onSendComplete}
          organizationId={orgId}
          organizationName={jobListing?.organizationName ?? ""}
          senderId={orgId}
          jobId={jobId}
          logoUrl={jobListing?.logoUrl ?? ""}
          onClose={() => setEmailManyRecipients(null)}
        />
      )}

      {emailTarget && (
        <EmailModal
          isOpen
          onClose={() => setEmailTarget(null)}
          candidateName={emailTarget.lead.applicantName}
          candidateEmail={emailTarget.lead.applicantEmail}
          organizationId={orgId}
          jobId={jobId}
          applicantId={emailTarget.lead.applicantId}
          organizationName={jobListing?.organizationName ?? ""}
          senderId={orgId}
          logoUrl={jobListing?.logoUrl ?? ""}
          confirmLabel={emailTarget.confirmLabel ?? "Send Email"}
          onSendComplete={emailTarget.onSendComplete ?? null}
          context={emailTarget.context}
        />
      )}
    </div>
  )
}

export default Leads
